import { tags } from "@/components/Forms/AddNoteDialog.tsx";
import { saveCaseWithTaskSchema } from "@/components/Forms/schema/saveCaseWithTaskSchema.ts";
import { useCaseTask } from "@/contexts/CaseTasksProvider.tsx";
import type { CaseTask } from "@/types/caseTask.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid2 as Grid,
    MenuItem,
} from "@mui/material";
import { RhfTextField } from "mui-rhf-integration";
import { type BaseSyntheticEvent, type ReactNode, useEffect, useMemo, useState } from "react";
import { FormProvider, type SubmitHandler, useForm } from "react-hook-form";
import type { z } from "zod";

export type SaveCaseWithTask = z.infer<typeof saveCaseWithTaskSchema>;

type Props = {
    caseStatus: string;
    handleSubmitWithTask: (
        taskId: string,
        note: string,
        caseStatus: string,
        event?: BaseSyntheticEvent,
    ) => void;
};
const SaveWithTaskButtonDialog = ({ handleSubmitWithTask }: Props): ReactNode => {
    const [open, setOpen] = useState(false);
    const { caseTasksPending } = useCaseTask();

    const tagsWithTasks = [...tags, "Task Completed", "Task In Progress"];

    const form = useForm<SaveCaseWithTask>({
        resolver: zodResolver(saveCaseWithTaskSchema),
    });

    const taskValue = form.watch("caseTaskUUID");

    const taskDescription = useMemo(() => {
        const foundCaseTask = caseTasksPending.find((task) => task.idUUID === taskValue);

        if (foundCaseTask) {
            return foundCaseTask.description;
        }

        return null;
    }, [caseTasksPending, taskValue]);

    const onSubmit: SubmitHandler<SaveCaseWithTask> = async (
        data: SaveCaseWithTask,
        event?: BaseSyntheticEvent,
    ) => {
        event?.stopPropagation();
        handleSubmitWithTask(data.caseTaskUUID, data.note, data.tag, event);
        closeDialog();
    };

    const closeDialog = () => {
        setOpen(false);
        form.reset({
            caseTaskUUID: "",
            tag: "",
            note: "",
        });
        form.clearErrors();
    };

    useEffect(() => {
        const foundTask = caseTasksPending.find((task) => task.idUUID === taskValue);

        if (foundTask) {
            form.setValue("tag", foundTask.defaultNoteTag ?? "");
        }
    }, [taskValue]);

    return (
        <Box>
            <Button
                variant="contained"
                onClick={() => setOpen(true)}
                sx={{
                    p: 1,
                    borderRadius: "8px",
                    fontWeight: 800,
                    mr: 2,
                }}
            >
                Save With Task
            </Button>
            <Dialog open={open} maxWidth="sm" fullWidth>
                <FormProvider {...form}>
                    <form>
                        <DialogTitle>Save with Task</DialogTitle>
                        <DialogContent dividers>
                            <Grid container spacing={2}>
                                <Grid size={12} sx={{ p: 1 }}>
                                    <RhfTextField
                                        select
                                        control={form.control}
                                        name={"caseTaskUUID"}
                                        label={"Task"}
                                        sx={{
                                            width: "100%",
                                            backgroundColor: "#fff",
                                        }}
                                    >
                                        {caseTasksPending.map((caseTask: CaseTask) => (
                                            <MenuItem
                                                key={`role-${caseTask.idUUID}`}
                                                value={`${caseTask.idUUID}`}
                                            >
                                                {caseTask.name}
                                            </MenuItem>
                                        ))}
                                    </RhfTextField>
                                </Grid>
                                {taskDescription && (
                                    <Grid size={12} sx={{ p: 1 }}>
                                        {taskDescription}
                                    </Grid>
                                )}
                                <Grid size={12} sx={{ p: 1 }}>
                                    <RhfTextField
                                        select
                                        control={form.control}
                                        name={"tag"}
                                        label={"Tag"}
                                        sx={{
                                            width: "100%",
                                            backgroundColor: "#fff",
                                        }}
                                    >
                                        {tagsWithTasks.map((tag) => (
                                            <MenuItem key={tag} value={tag}>
                                                {tag}
                                            </MenuItem>
                                        ))}
                                    </RhfTextField>
                                </Grid>
                                <Grid size={12} sx={{ p: 1 }}>
                                    <RhfTextField
                                        control={form.control}
                                        name={"note"}
                                        label={"Note"}
                                        multiline
                                        rows={4}
                                        sx={{
                                            width: "100%",
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={closeDialog}>Close</Button>
                            <Button variant={"contained"} onClick={form.handleSubmit(onSubmit)}>
                                Complete Task
                            </Button>
                        </DialogActions>
                    </form>
                </FormProvider>
            </Dialog>
        </Box>
    );
};

export default SaveWithTaskButtonDialog;
